import { useMediaQuery } from "react-responsive";
import {
  getMaxWidthForBreakpoint,
  TailwindBreakpoint,
} from "~/indigo-components/src/styles/breakpoints";

/**
 * Custom hook for detecting if the screen width is below a given breakpoint.
 * Defaults to the 2xl breakpoint (1536px) but can target any standard Tailwind breakpoint
 * or accept a numeric pixel value for exact width control.
 *
 * This hook should be called at a higher level (e.g., page or table component) and the
 * result passed down to child components to avoid creating multiple media query listeners.
 *
 * @param breakpointOrMaxWidth - Tailwind breakpoint key (sm, md, lg, xl, 2xl) or numeric maxWidth in pixels. Defaults to "2xl".
 * @returns boolean indicating if the screen width is below the provided breakpoint or maxWidth.
 *
 * @example
 * // In a parent component
 * const isSmallScreen = useIsSmallScreen(); // < 1536px (default "2xl")
 * const isCompactScreen = useIsSmallScreen("lg"); // < 1024px
 * const isNarrowScreen = useIsSmallScreen(1720); // < 1720px (exact pixel value)
 *
 * // Pass to children
 * <StatusCell isSmallScreen={isSmallScreen} ... />
 */
export const useIsSmallScreen = (
  breakpointOrMaxWidth: TailwindBreakpoint | number = "2xl",
) => {
  const maxWidth =
    typeof breakpointOrMaxWidth === "number"
      ? breakpointOrMaxWidth
      : getMaxWidthForBreakpoint(breakpointOrMaxWidth);
  return useMediaQuery({ maxWidth });
};
